import React, { useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { clampPopoverLeft } from '../Common/popover';

import { ReactComponent as EditIcon } from '@repo/common/assets/icons/16px/icon_16px_edit_outline.svg';
import { ReactComponent as DeleteIcon } from '@repo/common/assets/icons/16px/icon_16px_delete_outline.svg';
import { ReactComponent as ClockAlarmIcon } from '@repo/common/assets/icons/16px/icon_16px_clock-alarm_outline.svg';
import { ReactComponent as InvisibleIcon } from '@repo/common/assets/icons/16px/icon_16px_invisible-close_outline.svg';
import { ReactComponent as VisibleIcon } from '@repo/common/assets/icons/16px/icon_16px_visible_outline.svg';
import { ReactComponent as CheckCircleIcon } from '@repo/common/assets/icons/16px/icon_16px_check-circle_outline.svg';

import { BaseButton } from '@repo/common';
import {
  updateSlotHidden,
  updateSlotStatus,
} from '../../../api/workshop/workshop.api';

const ACTION_MESSAGES = {
  close: { title: 'Workshop closed.', width: '220px' },
  reopen: { title: 'Workshop reopened.', width: '242px' },
  completed: { title: 'Workshop marked as completed.', width: '340px' },
  hide: { title: 'Workshop hidden.', width: '222px' },
  unhide: { title: 'Workshop restored.', width: '234px' },
};

function OptionList({
  anchorRect,
  onClose,
  onDeletePermanentlyClick,
  onDeleteClick,
  onActionSuccess,
  isDeleted,
  isHidden,
  slotId,
  status,
}) {
  const listRef = useRef(null);
  const [activeItem, setActiveItem] = useState(null);

  if (!anchorRect) return null;

  const handleItemClick = async (e, action) => {
    e.preventDefault();
    e.stopPropagation();
    setActiveItem(action);

    if (action === 'deletePermanently') {
      handleDeletePermanentlyClick();
      return;
    }
    if (action === 'delete') {
      handleDeleteClick();
      return;
    }

    try {
      if (action === 'close') {
        await updateSlotStatus(slotId, 'CLOSED');
      } else if (action === 'reopen') {
        await updateSlotStatus(slotId, 'OPEN');
      } else if (action === 'completed') {
        await updateSlotStatus(slotId, 'COMPLETED');
      } else if (action === 'hide') {
        await updateSlotHidden(slotId, true);
      } else if (action === 'unhide') {
        await updateSlotHidden(slotId, false);
      }
      onActionSuccess?.(ACTION_MESSAGES[action]);
      onClose?.();
    } catch (err) {
      console.error('Action failed:', err);
    }
  };

  const handleDeletePermanentlyClick = () => {
    onDeletePermanentlyClick?.();
    onClose?.();
  };

  const handleDeleteClick = () => {
    onDeleteClick?.();
    onClose?.();
  };

  return (
    <>
      {anchorRect &&
        ReactDOM.createPortal(
          <>
            <div
              className='fixed inset-0 z-[999999] bg-transparent'
              onClick={(e) => {
                e.stopPropagation();
                onClose?.();
              }}
            />

            <div
              ref={listRef}
              onClick={(e) => e.stopPropagation()}
              style={{
                position: 'fixed',
                top: anchorRect.bottom + 6,
                left: clampPopoverLeft(
                  anchorRect.right - (isDeleted ? 200 : 160),
                  isDeleted ? 200 : 160,
                ),
              }}
              className={`
                ${isDeleted ? 'w-[200px]' : 'w-[160px]'}
                bg-grayscale-gray00 
                border border-grayscale-gray02 
                rounded-lg
                shadow-popup-04
                z-[1000000]
              `}
            >
              <div className='bg-grayscale-gray00 rounded-lg p-2'>
                <ul className='flex flex-col'>
                  <li>
                    <BaseButton
                      type='text'
                      size='m'
                      iconPosition='after'
                      icon={<EditIcon />}
                      width='100%'
                      separate={true}
                      leftalign={true}
                      onClick={(e) => handleItemClick(e, 'edit')}
                      bgclass={`bg-grayscale-gray00 border border-grayscale-gray00 ${
                        activeItem === 'edit'
                          ? 'bg-grayscale-gray02 text-text-primary'
                          : ''
                      }`}
                      textclass='typo-b14Strong'
                      hoverclass='hover:bg-grayscale-gray01 hover:text-text-primary'
                      activeclass='active:bg-grayscale-gray02 active:text-text-primary'
                      isDisabled
                    >
                      Edit
                    </BaseButton>
                  </li>

                  {!isDeleted && status === 'ONGOING' && (
                    <>
                      <li>
                        <BaseButton
                          type='text'
                          size='m'
                          iconPosition='after'
                          icon={<CheckCircleIcon />}
                          width='100%'
                          separate={true}
                          leftalign={true}
                          onClick={(e) => handleItemClick(e, 'completed')}
                          bgclass={`bg-grayscale-gray00 border border-grayscale-gray00 ${
                            activeItem === 'completed'
                              ? 'bg-grayscale-gray02 text-text-primary'
                              : ''
                          }`}
                          textclass='typo-b14Strong'
                          hoverclass='hover:bg-grayscale-gray01 hover:text-text-primary'
                          activeclass='active:bg-grayscale-gray02 active:text-text-primary'
                        >
                          Completed
                        </BaseButton>
                      </li>
                    </>
                  )}

                  {!isDeleted && status === 'OPEN' && !isHidden && (
                    <>
                      <li>
                        <BaseButton
                          type='text'
                          size='m'
                          iconPosition='after'
                          icon={<ClockAlarmIcon />}
                          width='100%'
                          separate={true}
                          leftalign={true}
                          onClick={(e) => handleItemClick(e, 'close')}
                          bgclass={`bg-grayscale-gray00 border border-grayscale-gray00 ${
                            activeItem === 'close'
                              ? 'bg-grayscale-gray02 text-text-primary'
                              : ''
                          }`}
                          textclass='typo-b14Strong'
                          hoverclass='hover:bg-grayscale-gray01 hover:text-text-primary'
                          activeclass='active:bg-grayscale-gray02 active:text-text-primary'
                        >
                          Close
                        </BaseButton>
                      </li>
                    </>
                  )}

                  {!isDeleted && !isHidden && status === 'CLOSED' && (
                    <li>
                      <BaseButton
                        type='text'
                        size='m'
                        iconPosition='after'
                        icon={<ClockAlarmIcon />}
                        width='100%'
                        separate={true}
                        leftalign={true}
                        onClick={(e) => handleItemClick(e, 'reopen')}
                        bgclass={`bg-grayscale-gray00 border border-grayscale-gray00 ${
                          activeItem === 'reopen'
                            ? 'bg-grayscale-gray02 text-text-primary'
                            : ''
                        }`}
                        textclass='typo-b14Strong'
                        hoverclass='hover:bg-grayscale-gray01 hover:text-text-primary'
                        activeclass='active:bg-grayscale-gray02 active:text-text-primary'
                      >
                        Reopen
                      </BaseButton>
                    </li>
                  )}

                  {!isDeleted && !isHidden && (
                    <li>
                      <BaseButton
                        type='text'
                        size='m'
                        iconPosition='after'
                        icon={<InvisibleIcon />}
                        width='100%'
                        separate={true}
                        leftalign={true}
                        onClick={(e) => handleItemClick(e, 'hide')}
                        bgclass={`bg-grayscale-gray00 border border-grayscale-gray00 ${
                          activeItem === 'hide'
                            ? 'bg-grayscale-gray02 text-text-primary'
                            : ''
                        }`}
                        textclass='typo-b14Strong'
                        hoverclass='hover:bg-grayscale-gray01 hover:text-text-primary'
                        activeclass='active:bg-grayscale-gray02 active:text-text-primary'
                      >
                        Hide
                      </BaseButton>
                    </li>
                  )}

                  {!isDeleted && isHidden && (
                    <li>
                      <BaseButton
                        type='text'
                        size='m'
                        iconPosition='after'
                        icon={<VisibleIcon />}
                        width='100%'
                        separate={true}
                        leftalign={true}
                        onClick={(e) => handleItemClick(e, 'unhide')}
                        bgclass={`bg-grayscale-gray00 border border-grayscale-gray00 ${
                          activeItem === 'unhide'
                            ? 'bg-grayscale-gray02 text-text-primary'
                            : ''
                        }`}
                        textclass='typo-b14Strong'
                        hoverclass='hover:bg-grayscale-gray01 hover:text-text-primary'
                        activeclass='active:bg-grayscale-gray02 active:text-text-primary'
                      >
                        Unhide
                      </BaseButton>
                    </li>
                  )}

                  <li>
                    <BaseButton
                      type='text'
                      size='m'
                      iconPosition='after'
                      icon={<DeleteIcon />}
                      width='100%'
                      separate={true}
                      leftalign={true}
                      onClick={(e) =>
                        handleItemClick(
                          e,
                          isDeleted ? 'deletePermanently' : 'delete',
                        )
                      }
                      bgclass={`bg-grayscale-gray00 border border-grayscale-gray00 ${
                        activeItem === 'deletePermanently'
                          ? 'bg-grayscale-gray02 text-text-primary'
                          : ''
                      }`}
                      textclass='typo-b14Strong'
                      hoverclass='hover:bg-grayscale-gray01 hover:text-text-primary'
                      activeclass='active:bg-grayscale-gray02 active:text-text-primary'
                    >
                      {isDeleted ? 'Delete permanently' : 'Delete'}
                    </BaseButton>
                  </li>
                </ul>
              </div>
            </div>
          </>,
          document.body,
        )}
    </>
  );
}
export default OptionList;
